import { useEffect } from 'react'
import { Filter, X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import '../styles/yardiInviteFiltersDrawer.css'
import type { YardiInviteFiltersDrawerProps } from '../types'

export function YardiInviteFiltersDrawer({ isOpen, onClose, list }: YardiInviteFiltersDrawerProps) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useBodyScrollLock(isOpen)

  const companyLocked = list.readOnlyControl.includes('company')
  const propertyLocked = list.readOnlyControl.includes('property')

  return (
    <>
      <div
        data-testid="yardi-invite-filters-drawer-backdrop"
        className={`fixed inset-0 z-[2147483646] bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />

      <div
        id="yardi-invite-filters"
        data-testid="yardi-invite-filters-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="yardi-invite-filters-title"
        className={`fixed top-0 right-0 z-[2147483647] flex h-full w-full touch-scroll flex-col overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-sm ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div
          data-testid="yardi-invite-filters-drawer-header"
          className="flex items-center justify-between border-b border-slate-100 px-6 py-4"
        >
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
            <h2
              id="yardi-invite-filters-title"
              className="text-lg font-bold"
              data-testid="yardi-invite-filters-drawer-title"
            >
              Filters
            </h2>
          </div>
          <button
            type="button"
            data-testid="yardi-invite-filters-drawer-close-button"
            onClick={onClose}
            className="-mr-2 rounded-full p-2 text-slate-400 transition-all hover:bg-slate-50 hover:text-slate-600"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          data-testid="yardi-invite-filters-drawer-content"
          className="flex-1 space-y-6 overflow-y-auto p-6"
        >
          <div
            className={`space-y-2 ${companyLocked ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="yardi-invite-filters-company-field"
          >
            <label
              className="text-xs font-semibold tracking-wider text-slate-500 uppercase"
              data-testid="yardi-invite-filters-company-label"
            >
              Company
            </label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                options={list.companyOptions}
                value={list.filterForm.company_id}
                onChange={(v) => {
                  const id = String(v)
                  if (id === list.filterForm.company_id) return
                  list.patchFilterForm({
                    company_id: id,
                    ...(!propertyLocked ? { property_id: '' } : {}),
                  })
                  list.companySelected(id)
                  list.filterSubmit()
                }}
                placeholder="All Company"
                searchPlaceholder="Search By Company Name"
                onSearch={list.searchCompany}
                searchDebounceMs={400}
                immediateSearchOnPaste
                onOpen={() => list.loadCompany('')}
                invokeSearchOnClose={false}
                triggerDataTestId="yardi-invite-list-company-select"
              />
            </div>
          </div>

          <div
            className={`space-y-2 ${propertyLocked ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="yardi-invite-filters-property-field"
          >
            <label
              className="text-xs font-semibold tracking-wider text-slate-500 uppercase"
              data-testid="yardi-invite-filters-property-label"
            >
              Property
            </label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                options={list.propertyOptions}
                value={list.filterForm.property_id}
                onChange={(v) => {
                  const id = String(v)
                  if (id === list.filterForm.property_id) return
                  list.patchFilterForm({ property_id: id })
                  list.filterSubmit()
                }}
                placeholder="All Properties"
                searchPlaceholder="Search By Property Name, City, State"
                onSearch={list.searchProperty}
                searchDebounceMs={400}
                immediateSearchOnPaste
                onOpen={() => list.loadProperty('')}
                invokeSearchOnClose={false}
                triggerDataTestId="yardi-invite-list-property-select"
              />
            </div>
          </div>
        </div>

        <div
          data-testid="yardi-invite-filters-drawer-footer"
          className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 bg-slate-50 p-4 sm:p-6"
        >
          <button
            type="button"
            data-testid="yardi-invite-filters-reset-all-button"
            onClick={() => list.resetFilter()}
            className="filter-drawer-reset-all"
          >
            Reset All
          </button>
          <button
            type="button"
            data-testid="yardi-invite-filters-done-button"
            onClick={onClose}
            className="flex-1 rounded-lg bg-[rgb(21_151_255)] py-2.5 text-sm font-semibold text-white shadow-md shadow-[rgba(21,151,255,0.22)] transition-all hover:bg-[#0d7acc]"
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}
